import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Phone,
  PhoneOff,
  PhoneIncoming,
  Pause,
  Play,
  Mic,
  MicOff,
  Users,
  PhoneForwarded,
  Hash,
  X,
  Minimize2,
  Maximize2,
  Plus,
  Shuffle,
  Circle,
  Settings,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Search,
  PhoneCall,
  Loader2,
  AlertCircle,
  Check,
  History,
  Moon,
  LogOut
, User, Building2, Target, ExternalLink } from "lucide-react";
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from "@easyflow/ui-frontend/components/ui/button";
import { Avatar, AvatarFallback } from "@easyflow/ui-frontend/components/ui/avatar";
import { Input } from "@easyflow/ui-frontend/components/ui/input";
import { ScrollArea } from "@easyflow/ui-frontend/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@easyflow/ui-frontend/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@easyflow/ui-frontend/components/ui/dialog";
import { Label } from "@easyflow/ui-frontend/components/ui/label";
import { Switch } from "@easyflow/ui-frontend/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@easyflow/ui-frontend/components/ui/select";
import { useSipPhone } from '@/contexts/SipPhoneContext';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import { useSocket } from '@/contexts/SocketContext';
import { getErrorMessage } from '../lib/utils';
import { getSipExtensionDndFeedback } from './sip-extension-dnd-feedback';
import { getSipExtensionQueueFeedback } from './sip-extension-queue-feedback';
import type { SipExtensionListItem, SipExtensionDndBody, SipExtensionQueueResponse } from '@easyflow/shared';


interface ActiveCallBLF {
  uuid: string;
  extension?: string;
  caller_number?: string;
  callee_number?: string;
  status: string;
  direction?: string;
}

interface ExtensionBLF {
  extension: string;
  name: string;
  status: 'available' | 'busy' | 'ringing' | 'dnd' | 'offline' | 'away';
  is_registered?: boolean;
  dnd_enabled?: boolean;
}

// Task #3713 — riga estensione dal DTO condiviso (getExtensions è tipizzato).

// Payload dei broadcast Socket.IO sullo stato delle estensioni.
interface ExtensionStatusChangeEvent {
  extension: string;
  is_registered?: boolean;
}

interface ExtensionUpdateEvent {
  extension: string;
  dnd_enabled?: boolean;
  queue_logged_in?: boolean;
}

// Voce dello storico chiamate (GET /cdr). Shape sul wire, distinta dal CDR
// canonico shared: usa destination_number/billsec/start_time.
interface CallHistoryItem {
  id?: string;
  call_direction?: 'inbound' | 'outbound' | 'internal' | 'emergency';
  caller_id_number?: string;
  destination_number?: string;
  start_time?: string | null;
  created_at?: string | null;
  billsec?: number | string | null;
}

interface SipDialpadProps {
  isOpen: boolean;
  onClose: () => void;
  extensions?: ExtensionBLF[];
}

export default function SipDialpad({ isOpen, onClose, extensions: externalExtensions }: SipDialpadProps) {
  const { user } = useAuth();
  const sipPhone = useSipPhone();
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { isConnected, on, off, joinRoom, leaveRoom } = useSocket();
  
  // CRM Customer Recognition from context (always active)
  const { recognizedCustomer, pendingDialNumber, clearPendingDialNumber } = sipPhone;
  
  // Debug: log when recognizedCustomer changes
  useEffect(() => {
    console.log('[SipDialpad] recognizedCustomer changed:', recognizedCustomer);
  }, [recognizedCustomer]);
  
  const [dialNumber, setDialNumber] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isDialing, setIsDialing] = useState(false);
  const [isMobileView, setIsMobileView] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const dialpadRef = useRef<HTMLDivElement>(null);

  const [isBlfExpanded, setIsBlfExpanded] = useState(true);
  const [blfSearchQuery, setBlfSearchQuery] = useState('');

  const userExtension = user?.extension;
  const userRole = user?.role;
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';

  const [transferDialog, setTransferDialog] = useState<{
    open: boolean;
    mode: 'blind' | 'attended';
    destination: string;
    step: 'select' | 'consulting' | 'complete';
    isProcessing: boolean;
  }>({ open: false, mode: 'blind', destination: '', step: 'select', isProcessing: false });

  const [conferenceDialog, setConferenceDialog] = useState<{
    open: boolean;
    thirdParty: string;
    isProcessing: boolean;
  }>({ open: false, thirdParty: '', isProcessing: false });

  const [isRecording, setIsRecording] = useState(false);
  const [recordingLoading, setRecordingLoading] = useState(false);

  const [isDndEnabled, setIsDndEnabled] = useState(false);
  const [isQueueLoggedIn, setIsQueueLoggedIn] = useState(false);
  const [dndLoading, setDndLoading] = useState(false);
  const [queueLoading, setQueueLoading] = useState(false);
  const [myExtensionId, setMyExtensionId] = useState<string | null>(null);

  const [internalExtensions, setInternalExtensions] = useState<ExtensionBLF[]>([]);
  const [blfActiveCalls, setBlfActiveCalls] = useState<ActiveCallBLF[]>([]);
  const endedCallUuidsRef = useRef<Set<string>>(new Set());
  
  const baseExtensions = externalExtensions || internalExtensions;

  const blfExtensions = useMemo(() => {
    return baseExtensions.map(ext => {
      const activeCall = blfActiveCalls.find(call =>
        call.extension === ext.extension ||
        call.callee_number === ext.extension ||
        call.caller_number === ext.extension
      );
      let status: ExtensionBLF['status'] = ext.dnd_enabled ? 'dnd' : (ext.is_registered ? 'available' : 'offline');
      if (activeCall) {
        status = activeCall.status === 'ringing' ? 'ringing' : 'busy';
      }
      return { ...ext, status };
    });
  }, [baseExtensions, blfActiveCalls]);

  useEffect(() => {
    if (isOpen && !externalExtensions) {
      apiClient.getExtensions({ limit: 100 }).then((res) => {
        // Task #3713 — getExtensions è tipizzato (data = { items, pagination }): niente cast.
        const data: SipExtensionListItem[] = res?.data?.items || [];
        setInternalExtensions(data.map((ext) => ({
          extension: ext.extension,
          name: ext.display_name || ext.extension,
          status: ext.is_registered ? (ext.dnd_enabled ? 'dnd' : 'available') : 'offline',
          is_registered: ext.is_registered,
          dnd_enabled: ext.dnd_enabled || false
        })));
      }).catch(() => {});
    }
  }, [isOpen, externalExtensions]);

  useEffect(() => {
    if (!isOpen || !isConnected || !user?.tenant_id) return;

    const callsRoom = `tenant:${user?.tenant_id}:calls`;
    joinRoom(callsRoom);

    const handleCallsUpdate = (calls: ActiveCallBLF[]) => {
      const valid = (calls || []).filter(c => !endedCallUuidsRef.current.has(c.uuid));
      setBlfActiveCalls(valid);
    };
    const handleCallCreated = (call: ActiveCallBLF) => {
      setBlfActiveCalls(prev => [...prev.filter(c => c.uuid !== call.uuid), call]);
    };
    const handleCallUpdated = (call: ActiveCallBLF) => {
      setBlfActiveCalls(prev => prev.map(c => c.uuid === call.uuid ? call : c));
    };
    const handleCallAnswered = (call: ActiveCallBLF) => {
      setBlfActiveCalls(prev => prev.map(c => c.uuid === call.uuid ? { ...c, ...call, status: call.status || 'answered' } : c));
    };
    const handleCallBridged = (call: ActiveCallBLF) => {
      setBlfActiveCalls(prev => prev.map(c => c.uuid === call.uuid ? { ...c, ...call, status: 'bridged' } : c));
    };
    const handleCallEnded = (data: { uuid: string }) => {
      endedCallUuidsRef.current.add(data.uuid);
      setTimeout(() => endedCallUuidsRef.current.delete(data.uuid), 30000);
      setBlfActiveCalls(prev => prev.filter(c => c.uuid !== data.uuid));
    };

    const handleExtensionStatusChange = (data: ExtensionStatusChangeEvent) => {
      setInternalExtensions(prev => prev.map(ext =>
        ext.extension === data.extension
          ? { ...ext, is_registered: data.is_registered }
          : ext
      ));
    };

    const handleExtensionUpdateBLF = (data: ExtensionUpdateEvent) => {
      setInternalExtensions(prev => prev.map(ext =>
        ext.extension === data.extension
          ? {
              ...ext,
              dnd_enabled: data.dnd_enabled !== undefined ? data.dnd_enabled : ext.dnd_enabled,
              queue_logged_in: data.queue_logged_in !== undefined ? data.queue_logged_in : (ext as ExtensionBLF & { queue_logged_in?: boolean }).queue_logged_in,
            }
          : ext
      ));
      const activeExt = sipPhone.activeExtension || userExtension;
      if (data.extension === activeExt) {
        if (data.dnd_enabled !== undefined) setIsDndEnabled(data.dnd_enabled);
        if (data.queue_logged_in !== undefined) setIsQueueLoggedIn(data.queue_logged_in);
      }
    };

    on('calls:update', handleCallsUpdate);
    on('call:created', handleCallCreated);
    on('call:updated', handleCallUpdated);
    on('call:answered', handleCallAnswered);
    on('call:bridged', handleCallBridged);
    on('call:ended', handleCallEnded);
    on('sip:extension:status_change', handleExtensionStatusChange);
    on('extension:update', handleExtensionUpdateBLF);

    return () => {
      leaveRoom(callsRoom);
      off('calls:update', handleCallsUpdate);
      off('call:created', handleCallCreated);
      off('call:updated', handleCallUpdated);
      off('call:answered', handleCallAnswered);
      off('call:bridged', handleCallBridged);
      off('call:ended', handleCallEnded);
      off('sip:extension:status_change', handleExtensionStatusChange);
      off('extension:update', handleExtensionUpdateBLF);
    };
  }, [isOpen, isConnected, user?.tenant_id, on, off, joinRoom, leaveRoom, userExtension, sipPhone.activeExtension]);

  // Call history state
  const [showCallHistory, setShowCallHistory] = useState(false);
  const [callHistoryTab, setCallHistoryTab] = useState<'all' | 'incoming' | 'outgoing'>('all');
  const [callHistory, setCallHistory] = useState<CallHistoryItem[]>([]);

  // Action feedback
  const [actionFeedback, setActionFeedback] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'warning';
    message: string;
  }>({ show: false, type: 'success', message: '' });

  const showFeedback = (type: 'success' | 'error' | 'warning', message: string) => {
    setActionFeedback({ show: true, type, message });
    setTimeout(() => setActionFeedback(prev => ({ ...prev, show: false })), type === 'warning' ? 8000 : 3000);
  };

  // SIP settings utente: solo preferenze locali (STUN/codec/UX). Il WSS è autoritario
  // dal backend via /voip/my-extension (SoT in env SIP_PUBLIC_WSS_URL), mai editabile
  // utente. Cleanup chiave legacy `sip_ws_server` una volta sola al mount.
  useEffect(() => {
    localStorage.removeItem('sip_ws_server');
  }, []);

  const [sipSettings, setSipSettings] = useState({
    stunServer: localStorage.getItem('sip_stun_server') || 'stun:stun.l.google.com:19302',
    debugMode: localStorage.getItem('sip_debug_mode') === 'true',
    autoAnswer: localStorage.getItem('sip_auto_answer') === 'true',
    codec: localStorage.getItem('sip_codec') || 'opus'
  });

  const handleSaveSettings = () => {
    localStorage.setItem('sip_stun_server', sipSettings.stunServer);
    localStorage.setItem('sip_debug_mode', String(sipSettings.debugMode));
    localStorage.setItem('sip_auto_answer', String(sipSettings.autoAnswer));
    localStorage.setItem('sip_codec', sipSettings.codec);
    setShowSettings(false);
  };

  const currentExtension = sipPhone.activeExtension || userExtension;

  // Load active extension state (DND, queue login status)
  useEffect(() => {
    if (!isOpen || !currentExtension) return;
    
    apiClient.getExtensions({ limit: 100 }).then((res) => {
      // Task #3713 — getExtensions è tipizzato (data = { items, pagination }): niente cast.
      const data: SipExtensionListItem[] = res?.data?.items || [];
      const activeExt = data.find((ext) => ext.extension === currentExtension);
      if (activeExt) {
        setMyExtensionId(activeExt.id);
        setIsDndEnabled(activeExt.dnd_enabled === true);
        setIsQueueLoggedIn(activeExt.queue_logged_in === true);
        console.log('[SipDialpad] Loaded extension state for', currentExtension, ':', { id: activeExt.id, dnd: activeExt.dnd_enabled, queueLoggedIn: activeExt.queue_logged_in });
      }
    }).catch((err) => console.error('[SipDialpad] Failed to load extension state:', err));
  }, [isOpen, currentExtension]);


  // DND toggle using same API as Switchboard cards
  const handleToggleDnd = async () => {
    if (!myExtensionId) {
      showFeedback('error', 'Estensione non trovata');
      return;
    }
    setDndLoading(true);
    try {
      const result = await apiClient.post(`/voip/sip-extensions/${myExtensionId}/dnd`, { enabled: !isDndEnabled } satisfies SipExtensionDndBody);
      const feedback = getSipExtensionDndFeedback(result.data);
      if (feedback.savedEnabled !== undefined) setIsDndEnabled(feedback.savedEnabled);
      showFeedback(feedback.type, feedback.message);
    } catch (err) {
      console.error('[SipDialpad] DND toggle failed:', err);
      showFeedback('error', 'Errore nel cambiare DND');
    } finally {
      setDndLoading(false);
    }
  };

  // Queue login using same API as Switchboard cards
  const handleQueueLogin = async () => {
    if (!myExtensionId) {
      showFeedback('error', 'Estensione non trovata');
      return;
    }
    setQueueLoading(true);
    try {
      const result = await apiClient.post<SipExtensionQueueResponse>(`/voip/sip-extensions/${myExtensionId}/queue-login`);
      const feedback = getSipExtensionQueueFeedback(result.data);
      if (feedback.savedLoggedIn !== undefined) setIsQueueLoggedIn(feedback.savedLoggedIn);
      showFeedback(feedback.type, feedback.message);
    } catch (err) {
      console.error('[SipDialpad] Queue login failed:', err);
      showFeedback('error', 'Errore nel login alla coda');
    } finally {
      setQueueLoading(false);
    }
  };

  // Queue logout using same API as Switchboard cards
  const handleQueueLogout = async () => {
    if (!myExtensionId) {
      showFeedback('error', 'Estensione non trovata');
      return;
    }
    setQueueLoading(true);
    try {
      const result = await apiClient.post<SipExtensionQueueResponse>(`/voip/sip-extensions/${myExtensionId}/queue-logout`);
      const feedback = getSipExtensionQueueFeedback(result.data);
      if (feedback.savedLoggedIn !== undefined) setIsQueueLoggedIn(feedback.savedLoggedIn);
      showFeedback(feedback.type, feedback.message);
    } catch (err) {
      console.error('[SipDialpad] Queue logout failed:', err);
      showFeedback('error', 'Errore nel logout dalla coda');
    } finally {
      setQueueLoading(false);
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpen && !sipPhone.isRegistered) {
      sipPhone.connect();
    }
  }, [isOpen, sipPhone.isRegistered, sipPhone]);

  useEffect(() => {
    setDialNumber('');
  }, [isOpen]);

  useEffect(() => {
    if (pendingDialNumber && isOpen) {
      setDialNumber(pendingDialNumber);
      clearPendingDialNumber();
    }
  }, [pendingDialNumber, isOpen, clearPendingDialNumber]);

  // Customer recognition is now handled by SipPhoneContext (always active)
  
  const navigateToCustomer = () => {
    if (!recognizedCustomer) return;
    const base = tenantSlug ? `/${tenantSlug}` : '';
    if (recognizedCustomer.type === 'b2c') {
      navigate(`${base}/crm?tab=contacts&id=${recognizedCustomer.id}`);
    } else if (recognizedCustomer.type === 'b2b') {
      navigate(`${base}/crm?tab=companies&id=${recognizedCustomer.id}`);
    } else if (recognizedCustomer.type === 'b2b_contact') {
      // Referente: apre la scheda del cliente B2B di appartenenza.
      navigate(`${base}/crm?tab=companies&id=${recognizedCustomer.customer_id ?? recognizedCustomer.id}`);
    } else {
      navigate(`${base}/crm?tab=leads&id=${recognizedCustomer.id}`);
    }
  };

  const getCustomerTypeLabel = (type: string) => {
    if (type === 'b2c') return 'Contatto';
    if (type === 'b2b') return 'Azienda';
    if (type === 'b2b_contact') return 'Referente';
    return 'Lead';
  };

  const getCustomerIcon = (type: string) => {
    if (type === 'b2c') return User;
    if (type === 'b2b') return Building2;
    if (type === 'b2b_contact') return User;
    return Target;
  };

  const handleKeypadPress = useCallback((digit: string) => {
    if (sipPhone.isInCall) {
      sipPhone.sendDTMF(digit);
    }
    setDialNumber(prev => prev + digit);
  }, [sipPhone]);

  // Supporto tastiera PC per digitare numeri
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignora se siamo in un input che non è il nostro
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' && !dialpadRef.current?.contains(target)) {
        return;
      }
      
      const validKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'];
      
      if (validKeys.includes(e.key)) {
        e.preventDefault();
        handleKeypadPress(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setDialNumber(prev => prev.slice(0, -1));
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, sipPhone.isInCall, handleKeypadPress]);

  const handleDragStart = (e: React.MouseEvent) => {
    if (!dialpadRef.current) return;
    setIsDragging(true);
    const rect = dialpadRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleDragMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    const newX = Math.max(0, Math.min(window.innerWidth - 520, e.clientX - dragOffset.x));
    const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y));
    setPosition({ x: newX, y: newY });
  }, [isDragging, dragOffset]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  const handleCall = async () => {
    if (dialNumber && !isDialing) {
      setIsDialing(true);
      try {
        // Check if this is a queue feature code - v2
        // NOTE: Feature codes *90/*91/*92 for queue/DND removed from SIP Phone
        // Use Switchboard extension cards for these functions (centralized, real-time synced)
        console.log('[SipDialpad] handleCall:', dialNumber);
        await sipPhone.call(dialNumber);
      } catch (error) {
        console.error('Call/feature code error:', error);
        showFeedback('error', 'Errore nell\'operazione');
      } finally {
        setIsDialing(false);
      }
    }
  };

  const handleHangup = async () => {
    await sipPhone.hangup();
    setDialNumber('');
  };

  const handleAnswer = async () => {
    await sipPhone.answer();
  };

  // Pickup - Intercept call on another extension (using *8 + extension)
  const handlePickup = async (extension: string) => {
    if (!sipPhone.isInCall && !isDialing) {
      setIsDialing(true);
      try {
        const pickupCode = `*8${extension}`;
        await sipPhone.call(pickupCode);
        showFeedback('success', `Pickup ${extension}`);
      } catch (error) {
        showFeedback('error', 'Pickup fallito');
      } finally {
        setIsDialing(false);
      }
    }
  };

  // Blind Transfer
  const handleBlindTransfer = async () => {
    if (!transferDialog.destination) return;
    setTransferDialog(prev => ({ ...prev, isProcessing: true }));
    try {
      const result = await sipPhone.transfer(transferDialog.destination);
      if (result) {
        showFeedback('success', `Trasferito a ${transferDialog.destination}`);
        setTransferDialog({ open: false, mode: 'blind', destination: '', step: 'select', isProcessing: false });
      } else {
        showFeedback('error', 'Trasferimento fallito');
      }
    } catch (error) {
      showFeedback('error', getErrorMessage(error) || 'Errore trasferimento');
    } finally {
      setTransferDialog(prev => ({ ...prev, isProcessing: false }));
    }
  };

  // Attended Transfer (via backend API)
  const handleAttendedTransfer = async () => {
    if (!transferDialog.destination) return;
    setTransferDialog(prev => ({ ...prev, isProcessing: true, step: 'consulting' }));
    try {
      const callUuid = sipPhone.callInfo?.remoteNumber;
      const response = await apiClient.post('/voip/calls/attended-transfer', {
        destination: transferDialog.destination,
        callUuid
      });
      if (response?.success) {
        showFeedback('success', `Trasferimento assistito a ${transferDialog.destination}`);
        setTransferDialog({ open: false, mode: 'blind', destination: '', step: 'select', isProcessing: false });
      } else {
        showFeedback('error', 'Trasferimento assistito fallito');
      }
    } catch (error) {
      showFeedback('error', getErrorMessage(error) || 'Errore trasferimento assistito');
    } finally {
      setTransferDialog(prev => ({ ...prev, isProcessing: false }));
    }
  };

  // Toggle Recording
  const handleToggleRecording = async () => {
    setRecordingLoading(true);
    try {
      const callUuid = sipPhone.callInfo?.remoteNumber;
      const action = isRecording ? 'stop' : 'start';
      const response = await apiClient.post('/voip/calls/recording', {
        action,
        callUuid
      });
      if (response?.success) {
        setIsRecording(!isRecording);
        showFeedback('success', isRecording ? 'Registrazione interrotta' : 'Registrazione avviata');
      } else {
        showFeedback('error', 'Errore registrazione');
      }
    } catch (error) {
      showFeedback('error', getErrorMessage(error) || 'Errore registrazione');
    } finally {
      setRecordingLoading(false);
    }
  };

  // Conference (3-way call via backend API)
  const handleConference = async () => {
    if (!conferenceDialog.thirdParty) return;
    setConferenceDialog(prev => ({ ...prev, isProcessing: true }));
    try {
      const callUuid = sipPhone.callInfo?.remoteNumber;
      const response = await apiClient.post('/voip/calls/conference', {
        thirdParty: conferenceDialog.thirdParty,
        callUuid
      });
      if (response?.success) {
        showFeedback('success', `Conferenza con ${conferenceDialog.thirdParty} avviata`);
        setConferenceDialog({ open: false, thirdParty: '', isProcessing: false });
      } else {
        showFeedback('error', 'Errore creazione conferenza');
      }
    } catch (error) {
      showFeedback('error', getErrorMessage(error) || 'Errore conferenza');
    } finally {
      setConferenceDialog(prev => ({ ...prev, isProcessing: false }));
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusIndicator = (ext: ExtensionBLF) => {
    if (ext.status === 'busy') return 'bg-red-500';
    if (ext.status === 'ringing') return 'bg-yellow-500';
    if (ext.status === 'dnd') return 'bg-red-600';
    if (ext.is_registered && ext.status === 'available') return 'bg-green-500';
    return 'bg-gray-400';
  };

  const getCallStateText = () => {
    switch (sipPhone.state) {
      case 'calling': return 'Chiamata in corso...';
      case 'ringing': return 'Squilla...';
      case 'incoming': return 'Chiamata in arrivo';
      case 'connected': return `${formatDuration(sipPhone.callInfo?.duration || 0)} - Connesso`;
      case 'on_hold': return 'In attesa';
      case 'registering': return 'Registrazione WebRTC...';
      case 'registered': return 'WebRTC attivo';
      case 'unregistered': return 'Non registrato — riconnessione...';
      case 'error': return 'Errore connessione';
      default: 
        if (sipPhone.fsIsRegistered) {
          const typeLabel = sipPhone.fsConnectionType === 'wss' ? 'WebRTC' : 
                           sipPhone.fsConnectionType === 'udp' ? 'SIP/UDP' :
                           sipPhone.fsConnectionType || 'SIP';
          return `Registrato (${typeLabel})`;
        }
        return 'Non registrato';
    }
  };

  // NOTE: DND and Queue Login/Logout functions removed from SIP Phone
  // These functions are now centralized in the Switchboard extension cards
  // to avoid duplication and ensure real-time synchronization via Socket.IO

  // Load call history (filtered by user's extension)
  const loadCallHistory = useCallback(async () => {
    try {
      // Filter by user's extension to show only their calls
      const extensionFilter = userExtension ? `&extension=${userExtension}` : '';
      const response = await apiClient.get<{ items?: CallHistoryItem[] } | CallHistoryItem[]>(`/cdr?limit=50${extensionFilter}`);
      const respData = response?.data;
      const data = Array.isArray(respData) ? respData : (respData?.items || []);
      setCallHistory(data);
    } catch (error) {
      console.error('Error loading call history:', error);
    }
  }, [userExtension]);

  // Load history when panel opens
  useEffect(() => {
    if (showCallHistory) {
      loadCallHistory();
    }
  }, [showCallHistory, loadCallHistory]);

  // Filter call history based on tab
  const filteredCallHistory = callHistory.filter(call => {
    if (callHistoryTab === 'all') return true;
    if (callHistoryTab === 'incoming') return call.call_direction === 'inbound';
    if (callHistoryTab === 'outgoing') return call.call_direction === 'outbound';
    return true;
  });

  if (!isOpen) return null;

  return (
    <TooltipProvider>
      <div
        ref={dialpadRef}
        style={isMobileView ? {
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
        } : {
          position: 'fixed',
          left: position.x,
          top: position.y,
          zIndex: 9999,
          userSelect: isDragging ? 'none' : 'auto'
        }}
        className={`transition-all duration-300 bg-card border-border ${
          isMobileView
            ? 'flex flex-col w-screen h-screen border-0 overflow-hidden'
            : `rounded-xl shadow-2xl overflow-hidden border ${
                isMinimized ? 'w-auto' : isBlfExpanded ? 'w-[520px]' : 'w-[300px]'
              }`
        }`}
      >
        {/* Header - Draggable */}
        <div
          className={`flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border select-none ${isMobileView ? '' : 'cursor-move'}`}
          onMouseDown={isMobileView ? undefined : handleDragStart}
        >
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-foreground">
              {sipPhone.activeExtension || userExtension || 'Telefono'}
              {(() => {
                const showExt = sipPhone.activeExtension || userExtension;
                const extInfo = blfExtensions.find(e => e.extension === showExt);
                return extInfo?.name ? ` - ${extInfo.name}` : '';
              })()}
              {sipPhone.activeExtension && sipPhone.activeExtension !== userExtension && (
                <span className="ml-1 text-xs text-orange-500">(admin)</span>
              )}
            </span>
            <div className={`h-2 w-2 rounded-full ${
              sipPhone.state === 'connected' ? 'bg-green-500 animate-pulse' :
              sipPhone.state === 'calling' || sipPhone.state === 'ringing' ? 'bg-yellow-500 animate-pulse' :
              sipPhone.state === 'incoming' ? 'bg-blue-500 animate-pulse' :
              sipPhone.state === 'registered' ? 'bg-green-500' :
              sipPhone.state === 'unregistered' ? 'bg-red-500 animate-pulse' :
              sipPhone.state === 'error' ? 'bg-red-500' :
              sipPhone.fsIsRegistered ? 'bg-green-500' :
              'bg-gray-400'
            }`} />
            <span className="text-xs text-muted-foreground">{getCallStateText()}</span>
          </div>
          <div className="flex items-center gap-1">
            {isAdmin && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-muted/50 ${showSettings ? 'text-primary' : ''}`}
                    onClick={() => setShowSettings(!showSettings)}
                  >
                    <Settings className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Impostazioni SIP</TooltipContent>
              </Tooltip>
            )}
            <Button
              variant="ghost"
              size="icon"
              className={`${isMobileView ? 'hidden' : 'h-6 w-6'} text-muted-foreground hover:text-foreground hover:bg-muted/50`}
              onClick={() => setIsMinimized(!isMinimized)}
            >
              {isMinimized ? <Maximize2 className="h-3.5 w-3.5" /> : <Minimize2 className="h-3.5 w-3.5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={`${isMobileView ? 'h-11 w-11' : 'h-6 w-6'} text-muted-foreground hover:text-destructive hover:bg-muted/50`}
              onClick={onClose}
            >
              <X className={isMobileView ? 'h-6 w-6' : 'h-3.5 w-3.5'} />
            </Button>
          </div>
        </div>

        {/* Content */}
        {!isMinimized && (
          <div className={isMobileView ? 'flex flex-col flex-1 min-h-0 overflow-y-auto' : 'flex'}>
            {/* Settings Panel */}
            {showSettings && isAdmin && (
              <div className="flex-1 p-4 border-r border-border">
                <div className="flex items-center gap-2 mb-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => setShowSettings(false)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <h3 className="text-sm font-semibold">Impostazioni SIP</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs">STUN Server</Label>
                    <Input
                      value={sipSettings.stunServer}
                      onChange={(e) => setSipSettings(s => ({ ...s, stunServer: e.target.value }))}
                      placeholder="stun:stun.l.google.com:19302"
                      className="h-8 text-xs"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs">Codec Audio</Label>
                    <Select
                      value={sipSettings.codec}
                      onValueChange={(v) => setSipSettings(s => ({ ...s, codec: v }))}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="opus">Opus (consigliato)</SelectItem>
                        <SelectItem value="pcmu">G.711 uLaw (PCMU)</SelectItem>
                        <SelectItem value="pcma">G.711 aLaw (PCMA)</SelectItem>
                        <SelectItem value="g722">G.722 HD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Auto-risposta</Label>
                    <Switch
                      checked={sipSettings.autoAnswer}
                      onCheckedChange={(v) => setSipSettings(s => ({ ...s, autoAnswer: v }))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Debug Mode</Label>
                    <Switch
                      checked={sipSettings.debugMode}
                      onCheckedChange={(v) => setSipSettings(s => ({ ...s, debugMode: v }))}
                    />
                  </div>
                  
                  <Button
                    className="w-full h-8 text-xs"
                    onClick={handleSaveSettings}
                  >
                    Salva Impostazioni
                  </Button>
                </div>
              </div>
            )}
            
            {/* Left Panel - Phone Controls (fixed width on desktop, full width on mobile) */}
            {!showSettings && <div className={isMobileView ? 'w-full flex-1 p-4 sm:p-5' : 'w-72 flex-shrink-0 p-4'}>
              {/* Task #1336 — badge supervisione attiva (Listen/Whisper/Barge) */}
              {sipPhone.supervisedSession && (
                <div className="mb-2 p-2 rounded-lg border border-amber-400 bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 text-xs flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                  <span className="font-semibold uppercase tracking-wide">Supervisione attiva</span>
                  <span>· modalità <strong>{sipPhone.supervisedSession.mode}</strong></span>
                  {sipPhone.supervisedSession.supervisorExtension && (
                    <span>· da <strong>{sipPhone.supervisedSession.supervisorExtension}</strong></span>
                  )}
                </div>
              )}

              {/* Call Info (only when in call or incoming) */}
              {(sipPhone.isInCall || sipPhone.state === 'incoming') && (
                <div className="flex items-center gap-3 mb-3 p-2 bg-muted/20 rounded-lg border border-border">
                  <Avatar className="h-10 w-10 border border-border">
                    <AvatarFallback className={`text-white text-sm font-semibold ${
                      recognizedCustomer 
                        ? recognizedCustomer.type === 'b2c' ? 'bg-gradient-to-br from-green-500 to-emerald-600'
                        : recognizedCustomer.type === 'b2b' ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
                        : recognizedCustomer.type === 'b2b_contact' ? 'bg-gradient-to-br from-purple-500 to-violet-600'
                        : 'bg-gradient-to-br from-orange-500 to-amber-600'
                        : 'bg-gradient-to-br from-blue-500 to-purple-600'
                    }`}>
                      {recognizedCustomer?.name?.charAt(0) || sipPhone.callInfo?.remoteName?.charAt(0) || sipPhone.callInfo?.remoteNumber?.slice(-2) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    {recognizedCustomer ? (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-foreground truncate">
                            {recognizedCustomer.name}
                          </span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                            recognizedCustomer.type === 'b2c' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : recognizedCustomer.type === 'b2b' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : recognizedCustomer.type === 'b2b_contact' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                            : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                          }`}>
                            {getCustomerTypeLabel(recognizedCustomer.type)}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">{sipPhone.callInfo?.remoteNumber}</div>
                        {recognizedCustomer.company_name && (
                          <div className="text-xs text-muted-foreground truncate">{recognizedCustomer.company_name}</div>
                        )}
                        {recognizedCustomer.role && (
                          <div className="text-[10px] text-muted-foreground truncate">{recognizedCustomer.role}</div>
                        )}
                        {recognizedCustomer.lead_stage && (
                          <div className="flex items-center gap-1 mt-1">
                            <span 
                              className="w-2 h-2 rounded-full" 
                              style={{ backgroundColor: recognizedCustomer.lead_stage_color || '#6b7280' }}
                            />
                            <span className="text-[10px] text-muted-foreground">{recognizedCustomer.lead_stage}</span>
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs px-2 mt-1 text-primary hover:text-primary/80"
                          onClick={navigateToCustomer}
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          Apri scheda
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="text-sm font-semibold text-foreground truncate">
                          {sipPhone.callInfo?.remoteName || sipPhone.callInfo?.remoteNumber || dialNumber}
                        </div>
                        {sipPhone.callInfo?.remoteName && (
                          <div className="text-xs text-muted-foreground">{sipPhone.callInfo.remoteNumber}</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Number Input */}
              {!sipPhone.isInCall && (
                <div className="mb-3">
                  <Input
                    value={dialNumber}
                    onChange={(e) => setDialNumber(e.target.value)}
                    className="text-center text-xl font-mono bg-muted/20 border-border text-foreground placeholder:text-muted-foreground h-11"
                    placeholder="Numero o estensione..."
                    onKeyDown={(e) => e.key === 'Enter' && dialNumber && handleCall()}
                  />
                </div>
              )}

              {/* 3x3 Action Buttons */}
              <div className={`grid grid-cols-3 gap-2 mb-3 ${isMobileView ? 'gap-2.5' : ''}`}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline"
                      className={`h-14 flex flex-col gap-1 border-border text-foreground ${
                        sipPhone.callInfo?.isHeld ? 'bg-yellow-500/20 border-yellow-500' : 'bg-muted/10 hover:bg-muted/30'
                      }`}
                      onClick={() => sipPhone.toggleHold()}
                      disabled={!sipPhone.isInCall}
                    >
                      {sipPhone.callInfo?.isHeld ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
                      <span className="text-[10px] font-medium">Hold</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{sipPhone.callInfo?.isHeld ? 'Riprendi' : 'Metti in attesa'}</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline"
                      className={`h-14 flex flex-col gap-1 border-border text-foreground ${
                        sipPhone.callInfo?.isMuted ? 'bg-red-500/20 border-red-500' : 'bg-muted/10 hover:bg-muted/30'
                      }`}
                      onClick={() => sipPhone.toggleMute()}
                      disabled={!sipPhone.isInCall}
                    >
                      {sipPhone.callInfo?.isMuted ? <MicOff className="h-5 w-5 text-red-500" /> : <Mic className="h-5 w-5" />}
                      <span className="text-[10px] font-medium">Mute</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{sipPhone.callInfo?.isMuted ? 'Attiva microfono' : 'Silenzia'}</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline"
                      className="h-14 flex flex-col gap-1 bg-muted/10 hover:bg-muted/30 border-border text-foreground"
                      disabled={sipPhone.isInCall}
                    >
                      <Plus className="h-5 w-5" />
                      <span className="text-[10px] font-medium">New call</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Nuova chiamata</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline"
                      className="h-14 flex flex-col gap-1 bg-muted/10 hover:bg-muted/30 border-border text-foreground"
                      disabled={!sipPhone.isInCall}
                      onClick={() => setConferenceDialog({ open: true, thirdParty: '', isProcessing: false })}
                    >
                      <Users className="h-5 w-5" />
                      <span className="text-[10px] font-medium">Conference</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Conferenza a 3</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline"
                      className="h-14 flex flex-col gap-1 bg-muted/10 hover:bg-muted/30 border-border text-foreground"
                      disabled={!sipPhone.isInCall}
                      onClick={() => setTransferDialog({ open: true, mode: 'blind', destination: '', step: 'select', isProcessing: false })}
                    >
                      <PhoneForwarded className="h-5 w-5" />
                      <span className="text-[10px] font-medium">Transfer</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Trasferimento cieco</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline"
                      className="h-14 flex flex-col gap-1 bg-muted/10 hover:bg-muted/30 border-border text-foreground"
                      disabled={!sipPhone.isInCall}
                      onClick={() => setTransferDialog({ open: true, mode: 'attended', destination: '', step: 'select', isProcessing: false })}
                    >
                      <Shuffle className="h-5 w-5" />
                      <span className="text-[10px] font-medium">Att. transfer</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Trasferimento assistito</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline"
                      className={`h-14 flex flex-col gap-1 border-border text-foreground ${
                        isRecording ? 'bg-red-500/20 border-red-500' : 'bg-muted/10 hover:bg-muted/30'
                      }`}
                      disabled={!sipPhone.isInCall || recordingLoading}
                      onClick={handleToggleRecording}
                    >
                      {recordingLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Circle className={`h-5 w-5 ${isRecording ? 'text-red-500 fill-red-500' : ''}`} />
                      )}
                      <span className="text-[10px] font-medium">{isRecording ? 'Stop Rec' : 'Record'}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{isRecording ? 'Ferma registrazione' : 'Avvia registrazione'}</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline"
                      className={`h-14 flex flex-col gap-1 border-border text-foreground ${
                        showKeypad ? 'bg-primary/20 border-primary' : 'bg-muted/10 hover:bg-muted/30'
                      }`}
                      onClick={() => setShowKeypad(!showKeypad)}
                    >
                      <Hash className="h-5 w-5" />
                      <span className="text-[10px] font-medium">Keypad</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Tastierino numerico</TooltipContent>
                </Tooltip>

                {/* DND Button - synced with Switchboard cards */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline"
                      className={`h-14 flex flex-col gap-1 border-border text-foreground ${
                        isDndEnabled ? 'bg-orange-500/20 border-orange-500 text-orange-500' : 'bg-muted/10 hover:bg-muted/30'
                      }`}
                      onClick={handleToggleDnd}
                      disabled={dndLoading || !myExtensionId}
                    >
                      {dndLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Moon className="h-5 w-5" />}
                      <span className="text-[10px] font-medium">DND</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{isDndEnabled ? 'Disattiva DND' : 'Attiva DND'}</TooltipContent>
                </Tooltip>

                {/* Queue Login/Logout Button - synced with Switchboard cards */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline"
                      className={`h-14 flex flex-col gap-1 border-border text-foreground ${
                        isQueueLoggedIn ? 'bg-blue-500/20 border-blue-500 text-blue-500' : 'bg-muted/10 hover:bg-muted/30'
                      }`}
                      onClick={isQueueLoggedIn ? handleQueueLogout : handleQueueLogin}
                      disabled={queueLoading || !myExtensionId}
                    >
                      {queueLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogOut className="h-5 w-5" />}
                      <span className="text-[10px] font-medium">{isQueueLoggedIn ? 'Logout' : 'Login'}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{isQueueLoggedIn ? 'Logout dalla coda' : 'Login in coda'}</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline"
                      className={`h-14 flex flex-col gap-1 border-border text-foreground ${
                        showCallHistory ? 'bg-primary/20 border-primary' : 'bg-muted/10 hover:bg-muted/30'
                      }`}
                      onClick={() => setShowCallHistory(!showCallHistory)}
                    >
                      <History className="h-5 w-5" />
                      <span className="text-[10px] font-medium">Storico</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Storico chiamate</TooltipContent>
                </Tooltip>
              </div>

              {/* Numeric Keypad (when enabled) */}
              {showKeypad && (
                <div className={`grid grid-cols-3 ${isMobileView ? 'gap-2 p-3' : 'gap-1.5 p-2'} mb-3 bg-muted/10 rounded-lg border border-border`}>
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map(digit => (
                    <Button
                      key={digit}
                      variant="outline"
                      className={`${isMobileView ? 'h-14 text-xl' : 'h-10 text-lg'} font-semibold border border-border hover:bg-muted/30 hover:border-primary/50 text-foreground transition-colors`}
                      onClick={() => handleKeypadPress(digit)}
                    >
                      {digit}
                    </Button>
                  ))}
                </div>
              )}

              {/* Call History Panel */}
              {showCallHistory && (
                <div className="mb-3 bg-muted/10 rounded-lg border border-border overflow-hidden">
                  {/* Tabs */}
                  <div className="flex border-b border-border">
                    <button
                      className={`flex-1 py-2 text-xs font-medium transition-colors ${
                        callHistoryTab === 'all' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted/20'
                      }`}
                      onClick={() => setCallHistoryTab('all')}
                    >
                      Tutte
                    </button>
                    <button
                      className={`flex-1 py-2 text-xs font-medium transition-colors ${
                        callHistoryTab === 'incoming' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted/20'
                      }`}
                      onClick={() => setCallHistoryTab('incoming')}
                    >
                      Ricevute
                    </button>
                    <button
                      className={`flex-1 py-2 text-xs font-medium transition-colors ${
                        callHistoryTab === 'outgoing' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted/20'
                      }`}
                      onClick={() => setCallHistoryTab('outgoing')}
                    >
                      Effettuate
                    </button>
                  </div>
                  {/* Call List */}
                  <ScrollArea className="h-[180px]">
                    <div className="p-2 space-y-1">
                      {filteredCallHistory.length === 0 ? (
                        <div className="text-center text-xs text-muted-foreground py-4">
                          Nessuna chiamata
                        </div>
                      ) : (
                        filteredCallHistory.slice(0, 20).map((call, idx) => (
                          <div
                            key={call.id || idx}
                            className="flex items-center gap-2 p-2 rounded hover:bg-muted/20 cursor-pointer transition-colors"
                            onClick={() => {
                              const number = call.call_direction === 'inbound' ? call.caller_id_number : call.destination_number;
                              // BUG NOTATO: `number` può essere undefined (campi wire opzionali);
                              // il setter è tipizzato `string`. Comportamento invariato via cast.
                              setDialNumber(number as string);
                              setShowCallHistory(false);
                            }}
                          >
                            <div className={`flex-shrink-0 ${call.call_direction === 'inbound' ? 'text-blue-500' : 'text-green-500'}`}>
                              {call.call_direction === 'inbound' ? (
                                <PhoneIncoming className="h-4 w-4" />
                              ) : (
                                <PhoneCall className="h-4 w-4" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">
                                {call.call_direction === 'inbound' ? call.caller_id_number : call.destination_number}
                              </div>
                              <div className="text-[10px] text-muted-foreground">
                                {new Date((call.start_time || call.created_at) as string).toLocaleString('it-IT', {
                                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                                })}
                                {call.billsec ? ` - ${formatDuration(Number(call.billsec))}` : ''}
                              </div>
                            </div>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-green-500 hover:bg-green-500/10"
                              onClick={async (e) => {
                                e.stopPropagation();
                                const number = call.call_direction === 'inbound' ? call.caller_id_number : call.destination_number;
                                if (number) {
                                  setShowCallHistory(false);
                                  await sipPhone.call(number);
                                }
                              }}
                            >
                              <Phone className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Call/Hangup Button */}
              <div className="flex gap-2">
                {!sipPhone.isInCall && dialNumber && (
                  <>
                    <Button
                      variant="outline"
                      className="h-12 w-12 bg-muted/10 hover:bg-muted/30 text-foreground border-border"
                      onClick={() => setDialNumber(prev => prev.slice(0, -1))}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                    <Button
                      className="h-12 flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold text-base"
                      onClick={handleCall}
                      disabled={isDialing}
                    >
                      {isDialing ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Connessione...
                        </>
                      ) : (
                        <>
                          <Phone className="h-5 w-5 mr-2" />
                          Chiama
                        </>
                      )}
                    </Button>
                  </>
                )}
                
                {sipPhone.state === 'incoming' && (
                  <div className="flex gap-2 w-full">
                    <Button
                      className="h-12 flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold"
                      onClick={handleAnswer}
                    >
                      <PhoneIncoming className="h-5 w-5 mr-2" />
                      Rispondi
                    </Button>
                    <Button
                      className="h-12 flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold"
                      onClick={handleHangup}
                    >
                      <PhoneOff className="h-5 w-5 mr-2" />
                      Rifiuta
                    </Button>
                  </div>
                )}

                {(sipPhone.state === 'connected' || sipPhone.state === 'calling' || sipPhone.state === 'ringing' || sipPhone.state === 'on_hold') && (
                  <Button
                    className="h-12 w-full bg-red-600 hover:bg-red-700 text-white font-semibold text-base"
                    onClick={handleHangup}
                  >
                    <PhoneOff className="h-5 w-5 mr-2" />
                    Termina
                  </Button>
                )}

                {!sipPhone.isInCall && !dialNumber && (
                  <Button
                    className="h-12 w-full bg-green-600/50 text-white/70 font-semibold text-base cursor-not-allowed"
                    disabled
                  >
                    <Phone className="h-5 w-5 mr-2" />
                    Chiama
                  </Button>
                )}
              </div>
            </div>}

            {/* BLF Toggle Button - Shows when BLF is collapsed (desktop only) */}
            {!isMobileView && !isBlfExpanded && (
              <div className="absolute right-0 top-1/2 -translate-y-1/2 z-10">
                <button
                  onClick={() => setIsBlfExpanded(true)}
                  className="flex items-center justify-center w-6 h-16 bg-gradient-to-r from-primary/80 to-primary rounded-l-lg shadow-lg hover:from-primary hover:to-primary/90 transition-all group"
                  title="Mostra interni"
                >
                  <Users className="h-4 w-4 text-primary-foreground group-hover:scale-110 transition-transform" />
                </button>
              </div>
            )}

            {/* Right Panel - BLF (Complete collapse when closed, hidden on mobile) */}
            {!isMobileView && isBlfExpanded && (
              <div className="w-52 border-l border-border bg-gradient-to-b from-muted/10 to-muted/5 flex flex-col">
                {/* BLF Header - Professional Design */}
                <div className="px-3 py-2.5 border-b border-border bg-gradient-to-r from-primary/10 to-transparent">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-md bg-primary/20">
                        <Users className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <div>
                        <span className="text-xs font-semibold text-foreground">Interni</span>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-500"></span>
                            {blfExtensions.filter(e => e.status !== 'offline').length}
                          </span>
                          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                            <span className="h-1.5 w-1.5 rounded-full bg-gray-400"></span>
                            {blfExtensions.filter(e => e.status === 'offline').length}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setIsBlfExpanded(false)}
                      className="p-1 rounded hover:bg-muted/50 transition-colors"
                      title="Chiudi pannello"
                    >
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </div>
                </div>

                {/* Search Field - Enhanced */}
                <div className="px-3 py-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <input
                      type="text"
                      value={blfSearchQuery}
                      onChange={(e) => setBlfSearchQuery(e.target.value)}
                      placeholder="Cerca interno..."
                      className="w-full h-8 pl-8 pr-3 text-xs bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/50 focus:border-primary transition-all placeholder:text-muted-foreground/60"
                    />
                  </div>
                </div>

                {/* Extension List - Professional Cards */}
                <ScrollArea className="flex-1 px-2 pb-2">
                  <div className="space-y-1">
                    {blfExtensions
                      .filter(ext => ext.extension !== userExtension)
                      .filter(ext => 
                        !blfSearchQuery || 
                        ext.name.toLowerCase().includes(blfSearchQuery.toLowerCase()) ||
                        ext.extension.includes(blfSearchQuery)
                      )
                      .slice(0, 30)
                      .map(ext => {
                        const isRinging = ext.status === 'ringing';
                        const isBusy = ext.status === 'busy';
                        const isAvailable = ext.status === 'available';
                        
                        return (
                          <div
                            key={ext.extension}
                            className={`group relative flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer transition-all ${
                              isRinging 
                                ? 'bg-gradient-to-r from-orange-500/20 to-orange-400/10 border border-orange-500/30 animate-pulse' 
                                : isBusy
                                ? 'bg-gradient-to-r from-red-500/10 to-transparent border border-red-500/20 hover:border-red-500/40'
                                : isAvailable
                                ? 'bg-gradient-to-r from-green-500/10 to-transparent border border-green-500/20 hover:border-green-500/40 hover:from-green-500/20'
                                : 'bg-muted/20 border border-border hover:border-muted-foreground/30 hover:bg-muted/40'
                            }`}
                            onClick={() => {
                              if (!sipPhone.isInCall && !isRinging) {
                                setDialNumber(ext.extension);
                              }
                            }}
                          >
                            {/* Avatar with Status Ring */}
                            <div className="relative flex-shrink-0">
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                isRinging 
                                  ? 'bg-orange-500 text-white'
                                  : isBusy
                                  ? 'bg-red-500/80 text-white'
                                  : isAvailable
                                  ? 'bg-green-500/80 text-white'
                                  : 'bg-muted text-muted-foreground'
                              }`}>
                                {ext.name.charAt(0).toUpperCase()}
                              </div>
                              {/* Status dot */}
                              <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background ${
                                isRinging 
                                  ? 'bg-orange-500 animate-ping' 
                                  : getStatusIndicator(ext)
                              }`} />
                            </div>
                            
                            {/* Name & Extension */}
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold text-foreground truncate">
                                {ext.name}
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] text-muted-foreground font-mono">
                                  {ext.extension}
                                </span>
                                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                                  isRinging 
                                    ? 'bg-orange-500/20 text-orange-600'
                                    : isBusy
                                    ? 'bg-red-500/20 text-red-600'
                                    : isAvailable
                                    ? 'bg-green-500/20 text-green-600'
                                    : 'bg-muted text-muted-foreground'
                                }`}>
                                  {isRinging ? 'Squilla' : isBusy ? 'Occupato' : isAvailable ? 'Libero' : 'Offline'}
                                </span>
                              </div>
                            </div>

                            {/* Action Button */}
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                              {isRinging && !sipPhone.isInCall ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handlePickup(ext.extension);
                                        }}
                                        className="p-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white shadow-lg"
                                      >
                                        <PhoneCall className="h-3.5 w-3.5" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="left">
                                      <p>Preleva chiamata</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : !sipPhone.isInCall && (
                                <div className="p-1.5 rounded-lg bg-primary/20 text-primary">
                                  <Phone className="h-3.5 w-3.5" />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>
        )}

        {/* Action Feedback Toast */}
        {actionFeedback.show && (
          <div role="status" aria-live="polite" className={`absolute bottom-16 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-md px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm font-medium z-50 ${
            actionFeedback.type === 'success' ? 'bg-green-600 text-white'
              : actionFeedback.type === 'warning' ? 'bg-amber-100 text-amber-950 border border-amber-500' : 'bg-red-600 text-white'
          }`}>
            {actionFeedback.type === 'success' ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            {actionFeedback.message}
          </div>
        )}
      </div>

      {/* Transfer Dialog */}
      <Dialog open={transferDialog.open} onOpenChange={(open) => !open && setTransferDialog(prev => ({ ...prev, open: false }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {transferDialog.mode === 'blind' ? 'Trasferimento Cieco' : 'Trasferimento Assistito'}
            </DialogTitle>
            <DialogDescription>
              {transferDialog.mode === 'blind' 
                ? 'La chiamata verrà trasferita immediatamente alla destinazione.'
                : 'Potrai parlare con la destinazione prima di completare il trasferimento.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Destinazione</Label>
              <Input
                value={transferDialog.destination}
                onChange={(e) => setTransferDialog(prev => ({ ...prev, destination: e.target.value }))}
                placeholder="Numero o estensione..."
                className="font-mono"
              />
            </div>
            <div className="text-xs text-muted-foreground">
              Estensioni disponibili:
            </div>
            <ScrollArea className="h-32 border rounded-md p-2">
              <div className="space-y-1">
                {blfExtensions.filter(e => e.is_registered && e.extension !== userExtension).map(ext => (
                  <div 
                    key={ext.extension}
                    className="flex items-center justify-between p-2 rounded cursor-pointer hover:bg-muted/50"
                    onClick={() => setTransferDialog(prev => ({ ...prev, destination: ext.extension }))}
                  >
                    <span className="text-sm">{ext.name}</span>
                    <span className="text-xs text-muted-foreground font-mono">{ext.extension}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialog(prev => ({ ...prev, open: false }))}>
              Annulla
            </Button>
            <Button 
              onClick={transferDialog.mode === 'blind' ? handleBlindTransfer : handleAttendedTransfer}
              disabled={!transferDialog.destination || transferDialog.isProcessing}
            >
              {transferDialog.isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Trasferimento...
                </>
              ) : (
                <>
                  <PhoneForwarded className="h-4 w-4 mr-2" />
                  Trasferisci
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conference Dialog */}
      <Dialog open={conferenceDialog.open} onOpenChange={(open) => !open && setConferenceDialog(prev => ({ ...prev, open: false }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Conferenza a 3</DialogTitle>
            <DialogDescription>
              Aggiungi un terzo partecipante alla chiamata corrente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Terzo partecipante</Label>
              <Input
                value={conferenceDialog.thirdParty}
                onChange={(e) => setConferenceDialog(prev => ({ ...prev, thirdParty: e.target.value }))}
                placeholder="Numero o estensione..."
                className="font-mono"
              />
            </div>
            <div className="text-xs text-muted-foreground">
              Estensioni disponibili:
            </div>
            <ScrollArea className="h-32 border rounded-md p-2">
              <div className="space-y-1">
                {blfExtensions.filter(e => e.is_registered && e.extension !== userExtension).map(ext => (
                  <div 
                    key={ext.extension}
                    className="flex items-center justify-between p-2 rounded cursor-pointer hover:bg-muted/50"
                    onClick={() => setConferenceDialog(prev => ({ ...prev, thirdParty: ext.extension }))}
                  >
                    <span className="text-sm">{ext.name}</span>
                    <span className="text-xs text-muted-foreground font-mono">{ext.extension}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConferenceDialog(prev => ({ ...prev, open: false }))}>
              Annulla
            </Button>
            <Button 
              onClick={handleConference}
              disabled={!conferenceDialog.thirdParty || conferenceDialog.isProcessing}
            >
              {conferenceDialog.isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Connessione...
                </>
              ) : (
                <>
                  <Users className="h-4 w-4 mr-2" />
                  Avvia Conferenza
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
